import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Table, ConfigProvider, Typography } from 'antd';
import Icon from '@ant-design/icons';
import { ReactComponent as Up } from '../../assets/icons/up.svg';
import { ReactComponent as UpBlack } from '../../assets/icons/up-black.svg';
import { ReactComponent as Down } from '../../assets/icons/down.svg';
import { ReactComponent as DownBlack } from '../../assets/icons/down-black.svg';
import { useTheme } from '../../context/ThemeContext';
import { DARK, HYPHEN, SORT } from '../../utilities/constants';
import FilterDropdown from './FilterDropdown';
import FilterIcon from './FilterIcon';
import FilterSelectDropdown from './FilterSelectDropdown';
import SortIcon from './SortIcon';
import EmptyCard from '../EmptyCard/EmptyCard';

const { Text } = Typography;

const ExpandIcon = ({ expanded, onExpand, record, theme }) => (
  <Icon
    component={
      expanded
        ? theme === DARK
          ? UpBlack
          : Up
        : theme === DARK
          ? Down
          : DownBlack
    }
    onClick={e => onExpand(record, e)}
  />
);

ExpandIcon.propTypes = {
  expanded: PropTypes.bool.isRequired,
  onExpand: PropTypes.func.isRequired,
  record: PropTypes.object.isRequired,
  theme: PropTypes.string.isRequired,
};

const getColumnSearchProps = (
  dataIndex,
  handleFilterSearch,
  handleReset,
  tableParams,
  placeholder
) => ({
  filterDropdown: props => (
    <FilterDropdown
      {...props}
      dataIndex={dataIndex}
      handleFilterSearch={handleFilterSearch}
      handleReset={handleReset}
      tableParams={tableParams}
      placeholder={placeholder}
    />
  ),
  filterIcon: () => (
    <FilterIcon dataIndex={dataIndex} tableParams={tableParams} />
  ),
});

const getCustomSortProps = (dataIndex, tableParams) => ({
  sorter: true,
  sortIcon: () => <SortIcon dataIndex={dataIndex} tableParams={tableParams} />,
});

const getColumnSelectFilterProps = (
  dataIndex,
  options,
  handleFilterSelect,
  handleReset,
  tableParams,
  placeholder
) => ({
  filterDropdown: props => (
    <FilterSelectDropdown
      {...props}
      dataIndex={dataIndex}
      options={options}
      handleFilterSelect={handleFilterSelect}
      handleReset={handleReset}
      tableParams={tableParams}
      placeholder={placeholder}
    />
  ),
  filterIcon: () => (
    <FilterIcon dataIndex={dataIndex} tableParams={tableParams} />
  ),
});

const LogsTable = ({
  data,
  columns,
  tableParams,
  setTableParams,
  loading,
  expandedRowRenderCallback,
  onExpand,
  expandable = false,
  className = '',
  reportsTable = false,
  selectedRow = null,
  expandedRow = null,
}) => {
  const { theme } = useTheme();
  const lastSortedColumnRef = useRef(null);

  const handleFilterSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    const newFilters = { ...tableParams.filters };
    if (!selectedKeys[0]) {
      // If value is empty string, remove the filter key
      delete newFilters[dataIndex];
    } else {
      // Else, assign the value
      newFilters[dataIndex] = selectedKeys[0];
    }
    setTableParams({
      ...tableParams,
      filters: newFilters,
      pagination: {
        ...tableParams.pagination,
        current: 1,
      },
    });
  };

  const handleSelectFilter = (selectedKeys, confirm, dataIndex) => {
    confirm();
    const newFilters = { ...tableParams.filters };
    if (!selectedKeys[0]) {
      delete newFilters[dataIndex];
    } else {
      newFilters[dataIndex] = selectedKeys[0];
    }
    setTableParams({
      ...tableParams,
      filters: newFilters,
      pagination: {
        ...tableParams.pagination,
        current: 1,
      },
    });
  };

  const handleReset = (clearFilters, setSelectedKeys, dataIndex, confirm) => {
    clearFilters();
    setSelectedKeys(['']);
    confirm();
    const newFilters = { ...tableParams.filters };
    delete newFilters[dataIndex];
    setTableParams({
      ...tableParams,
      filters: newFilters,
      pagination: {
        ...tableParams.pagination,
        current: 1,
      },
    });
  };

  const getSorterOption = order => {
    if (order === SORT.ASCEND_ANTD) {
      return SORT.ASCEND_BE;
    } else if (order === SORT.DESCEND_ANTD) {
      return SORT.DESCEND_BE;
    } else if (order === undefined) {
      return '';
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter && sorter?.columnKey && sorter?.order) {
      // Sorting is active for this column
      lastSortedColumnRef.current = sorter?.columnKey;
      setTableParams({
        ...tableParams,
        pagination: pagination,
        sort: {
          ...tableParams.sort,
          [sorter?.columnKey]: getSorterOption(sorter?.order),
        },
      });
    } else if (!sorter?.columnKey && lastSortedColumnRef.current) {
      // Sorting canceled for the last sorted column
      const newSort = { ...tableParams.sort };
      delete newSort[lastSortedColumnRef.current];
      setTableParams({
        ...tableParams,
        pagination: pagination,
        sort: newSort,
      });
      lastSortedColumnRef.current = null;
    } else {
      setTableParams({
        ...tableParams,
        pagination: pagination,
      });
    }
  };

  const addNullValueHandler = column => {
    if (column.key === 'form_actions') {
      return column;
    }
    const originalRender = column.render;
    return {
      ...column,
      render: (value, record) => {
        if (!value && !originalRender) {
          return HYPHEN;
        }
        return originalRender ? originalRender(value, record) : value;
      },
    };
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            rowExpandedBg: reportsTable
              ? '#D0D4D8'
              : 'var(--selected-bg-color, #D0D4D8)',
          },
        },
      }}
    >
      <Table
        loading={loading}
        dataSource={data}
        pagination={{
          ...tableParams.pagination,
          showTotal: (total, range) => `${range[0]} - ${range[1]}`,
        }}
        onChange={handleTableChange}
        className={className}
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: <EmptyCard />,
        }}
        rowClassName={(record, index) => {
          let className = index === 0 ? 'table-header-border' : '';
          if (record.key === selectedRow) {
            className += ' selected-row';
          }
          if (!record?.isActionable) {
            className += ' unactionable-row';
          }
          if (record.key === expandedRow) {
            className += ' expanded-row';
          }
          return className;
        }}
        {...(expandable && {
          expandable: {
            expandedRowKeys: expandedRow ? [expandedRow] : [],
            expandedRowRender: expandedRowRenderCallback,
            onExpand: onExpand,
            expandIcon: props => <ExpandIcon {...props} theme={theme} />,
          },
        })}
        {...(reportsTable && {
          expandable: {
            expandedRowKeys: tableParams?.expandedRowKeys,
            expandedRowRender: record => {
              const currentIndex = data.findIndex(
                item => item.key === record.key
              );
              const nextRecord =
                currentIndex < data.length - 1 ? data[currentIndex + 1] : null;
              return (
                <Text style={{ color: 'black', fontWeight: 700 }}>
                  {`${nextRecord?.date ?? '-'}`}
                </Text>
              );
            },
            expandIconColumnIndex: -1,
          },
        })}
        columns={columns.map(column => {
          let modifiedColumn = addNullValueHandler(column);

          // Both sort and selectFilter
          if (column?.sort && column?.selectFilter) {
            return {
              ...modifiedColumn,
              ...getCustomSortProps(column?.dataIndex, tableParams),
              ...getColumnSelectFilterProps(
                column?.dataIndex,
                column?.selectFilterOptions,
                (selectedKey, confirm, dataIndex) =>
                  handleSelectFilter(selectedKey, confirm, dataIndex),
                handleReset,
                tableParams,
                column?.title
              ),
            };
          } else if (column?.selectFilter) {
            return {
              ...modifiedColumn,
              ...getColumnSelectFilterProps(
                column?.dataIndex,
                column?.selectFilterOptions,
                (selectedKey, confirm, dataIndex) =>
                  handleSelectFilter(selectedKey, confirm, dataIndex),
                handleReset,
                tableParams,
                column?.title
              ),
            };
          } else if (column?.sort && column?.filter) {
            return {
              ...modifiedColumn,
              ...getCustomSortProps(column?.dataIndex, tableParams),
              ...getColumnSearchProps(
                column?.dataIndex,
                handleFilterSearch,
                handleReset,
                tableParams,
                column?.title
              ),
            };
          } else if (column?.filter) {
            return {
              ...modifiedColumn,
              ...getColumnSearchProps(
                column?.dataIndex,
                handleFilterSearch,
                handleReset,
                tableParams,
                column?.title
              ),
            };
          } else if (column?.sort) {
            return {
              ...modifiedColumn,
              ...getCustomSortProps(column?.dataIndex, tableParams),
            };
          } else {
            return modifiedColumn;
          }
        })}
      />
    </ConfigProvider>
  );
};

LogsTable.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array.isRequired,
  tableParams: PropTypes.object.isRequired,
  setTableParams: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  expandedRowRenderCallback: PropTypes.func,
  onExpand: PropTypes.func,
  expandable: PropTypes.bool,
  className: PropTypes.string,
  reportsTable: PropTypes.bool,
  selectedRow: PropTypes.any,
};

export default LogsTable;
